import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import RtlLayout from "layouts/rtl";
import AdminLayout from "layouts/admin";
import AuthLayout from "layouts/auth";
import AuthCarousel from "views/auth/AuthCarousel";
import ProtectedRoute from "components/auth/ProtectedRoute";
import GuestRoute from "components/auth/GuestRoute";
import FullPageSpinner from "components/auth/FullPageSpinner";
import { useAuth } from "contexts/AuthContext";
import routes from "routes.js";
import { getAllowedRoutes } from "utils/routeAccess";

// URLs are flat (/login, /live-incidents), so the route table is declared here
// once and the layouts render an <Outlet />. Previously each layout owned its
// own nested <Routes> and duplicated the filtering.
const App = () => {
  const { user, loading } = useAuth();

  const appRoutes = React.useMemo(() => getAllowedRoutes(routes, "app", user), [user]);
  const authRoutes = React.useMemo(() => getAllowedRoutes(routes, "auth", user), [user]);

  // Which routes exist depends on the signed-in user, so the table cannot be
  // built until the session is resolved. Rendering <Routes> against an empty
  // table would bounce a deep link to the catch-all before we know who they are.
  if (loading) return <FullPageSpinner />;

  const defaultPath = appRoutes[0]?.path;

  return (
    <Routes>
      <Route element={<GuestRoute />}>
        <Route element={<AuthLayout />}>
          {/* Carousel panels share one mounted component so the slide between
              them survives navigation; the child routes exist to set the URL. */}
          <Route element={<AuthCarousel />}>
            {authRoutes
              .filter((route) => route.carousel)
              .map((route) => (
                <Route key={route.path} path={route.path} element={null} />
              ))}
          </Route>
          {authRoutes
            .filter((route) => !route.carousel)
            .map((route) => (
              <Route key={route.path} path={route.path} element={route.component} />
            ))}
        </Route>
      </Route>

      <Route element={<ProtectedRoute />}>
        <Route element={<AdminLayout />}>
          {appRoutes.map((route) => (
            <Route key={route.path} path={route.path} element={route.component} />
          ))}
        </Route>
        <Route path="rtl/*" element={<RtlLayout />} />
      </Route>

      <Route path="/" element={<Landing defaultPath={defaultPath} />} />
      <Route path="*" element={<Landing defaultPath={defaultPath} />} />
    </Routes>
  );
};

// Handles "/" and anything unmatched: sends visitors wherever they belong
// rather than always to the sign-in page.
function Landing({ defaultPath }) {
  const { user } = useAuth();
  const location = useLocation();

  if (!user) {
    // Preserve the target so login can return them to it. An unmatched path
    // reaches here rather than ProtectedRoute, which would otherwise lose it.
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Authenticated but nothing is visible to them - usually a tenant with no
  // routes assigned. Redirecting would loop, so say so instead.
  if (!defaultPath) return <NoRoutesAvailable />;

  return <Navigate to={`/${defaultPath}`} replace />;
}

function NoRoutesAvailable() {
  const { logout } = useAuth();
  return (
    <div className="flex h-screen w-full flex-col items-center justify-center gap-4 bg-lightPrimary px-6 text-center dark:!bg-navy-900">
      <h1 className="text-2xl font-bold text-navy-700 dark:text-white">No views available</h1>
      <p className="max-w-md text-gray-600">
        Your account is signed in but has no views assigned. Contact an administrator
        if you believe this is wrong.
      </p>
      <button
        onClick={logout}
        className="rounded-xl bg-brand-500 px-5 py-2 font-medium text-white hover:bg-brand-600"
      >
        Sign out
      </button>
    </div>
  );
}

export default App;
